// Regras de exibição dos leads: origem, contato e CSV.
// Sem dependências: testado com `npm test` (node --test).

/** O que as regras precisam de um lead (o mesmo formato de api/leads.ts). */
export type LeadBasico = {
  enviadoEm: string
  marca: string
  fonte: string
  formulario: string | null
  paginaUrl: string | null
  paginaTitulo: string | null
  nome: string | null
  email: string | null
  telefone: string | null
  mensagem: string | null
  utmSource: string | null
  utmMedium: string | null
  utmCampaign: string | null
  utmTerm: string | null
  utmContent: string | null
  gclid: string | null
  fbclid: string | null
  referrer: string | null
}

/**
 * De onde veio, pelo que o site registrou. Nunca adivinha a campanha: sem UTM, o que
 * resta é o id de clique ou o site de onde a pessoa chegou; sem nada, "Sem origem registrada".
 */
export function origemDoLead(l: LeadBasico): string {
  if (l.utmSource) return [l.utmSource, l.utmMedium].filter(Boolean).join(' / ')
  if (l.gclid) return 'Google Ads (gclid)'
  if (l.fbclid) return 'Meta (fbclid)'
  const site = hostDe(l.referrer)
  if (site) return `Chegou de ${site}`
  return 'Sem origem registrada'
}

export function hostDe(url: string | null): string | null {
  if (!url) return null
  try {
    return new URL(url).hostname.replace(/^www\./, '') || null
  } catch {
    return null
  }
}

/** O caminho da página, para caber na tabela: "/acrilico/" em vez da URL inteira. */
export function caminhoDe(url: string | null): string | null {
  if (!url) return null
  try {
    const u = new URL(url)
    return u.pathname || '/'
  } catch {
    return url
  }
}

/**
 * Link de WhatsApp do telefone, no formato do wa.me (só dígitos, com DDI).
 * Número brasileiro sem DDI (10 ou 11 dígitos) ganha o 55. Curto demais: sem link.
 */
export function linkWhatsApp(telefone: string | null): string | null {
  if (!telefone) return null
  let d = telefone.replace(/\D/g, '')
  if (d.startsWith('00')) d = d.slice(2)
  if (d.length === 10 || d.length === 11) d = `55${d}`
  if (d.length < 12 || d.length > 15) return null
  return `https://wa.me/${d}`
}

/** Contagem por chave, da maior para a menor (empate: ordem alfabética). */
export function contarPor<T>(itens: T[], chave: (t: T) => string): Array<{ chave: string; total: number }> {
  const m = new Map<string, number>()
  for (const t of itens) {
    const k = chave(t)
    m.set(k, (m.get(k) ?? 0) + 1)
  }
  return [...m.entries()]
    .map(([k, total]) => ({ chave: k, total }))
    .sort((a, b) => b.total - a.total || a.chave.localeCompare(b.chave, 'pt-BR'))
}

const dataHoraSP = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo' })

/** 30/09/2026, 14:05 — sempre no horário de São Paulo. */
export const formatarDataHora = (iso: string) => dataHoraSP.format(new Date(iso))

// Excel em português abre CSV com ponto e vírgula; o BOM faz ele ler os acentos.
function celula(v: string | null | undefined): string {
  const s = (v ?? '').replace(/\r?\n/g, ' ')
  // Fórmula no início da célula vira texto (injeção de fórmula ao abrir no Excel).
  const seguro = /^[=+\-@\t]/.test(s) ? `'${s}` : s
  return /[";]/.test(seguro) ? `"${seguro.replaceAll('"', '""')}"` : seguro
}

export const COLUNAS_CSV = [
  'Data (São Paulo)', 'Marca', 'Site', 'Formulário', 'Página', 'Título da página', 'Nome', 'E-mail', 'Telefone', 'Mensagem',
  'Origem', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid', 'Chegou de',
]

export function csvDeLeads(leads: LeadBasico[]): string {
  const linhas = leads.map((l) =>
    [
      formatarDataHora(l.enviadoEm), l.marca, l.fonte, l.formulario, l.paginaUrl, l.paginaTitulo, l.nome, l.email, l.telefone, l.mensagem,
      origemDoLead(l), l.utmSource, l.utmMedium, l.utmCampaign, l.utmTerm, l.utmContent, l.gclid, l.fbclid, l.referrer,
    ]
      .map(celula)
      .join(';'),
  )
  return '﻿' + [COLUNAS_CSV.map(celula).join(';'), ...linhas].join('\r\n')
}
